/**
 * What a publication is, in the monitor's terms: how runs of notices fold
 * into rows, which pile and state each row sits in, how much it touches the
 * firm, and when a source has gone quiet. Pure — shared by the publications
 * list and the frameworks table, so both count the same way.
 *
 * @module components/regulation/monitor/publications
 */
import type { Monitor } from "../../../state/governance";
import type { RegulatoryUpdate } from "../../../state/policies";

export const DAY = 24 * 60 * 60 * 1000;
export const STALE_DAYS = 14;

export const formatDate = (value: string | null | undefined): string => {
  if (!value) return "—";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? "—"
    : parsed.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

export const relative = (value: string | null, now: number): string => {
  if (!value) return "never";
  const at = Date.parse(value);
  if (Number.isNaN(at)) return "—";
  const days = Math.floor((now - at) / DAY);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 14) return `${days} days ago`;
  return formatDate(value);
};

/** A monitor that is on but has not been read within the stale window. */
export const isQuiet = (monitor: Pick<Monitor, "enabled" | "lastCheckedAt">, now: number): boolean => {
  if (!monitor.enabled) return false;
  const at = monitor.lastCheckedAt ? Date.parse(monitor.lastCheckedAt) : Number.NaN;
  return Number.isNaN(at) || now - at > STALE_DAYS * DAY;
};

/**
 * Regulators publish some things in runs — a sanctions notice a week, each
 * its own page. Fourteen identical rows is the firehose every monitoring
 * product is disliked for, so publications from one monitor that share a
 * title fold into one row with a count; expanding it lists each.
 */
export interface PublicationGroup {
  readonly key: string;
  readonly lead: RegulatoryUpdate;
  readonly items: ReadonlyArray<RegulatoryUpdate>;
}

export function groupPublications(updates: ReadonlyArray<RegulatoryUpdate>): PublicationGroup[] {
  const groups = new Map<string, RegulatoryUpdate[]>();
  const order: string[] = [];
  for (const update of updates) {
    const key = `${update.monitorId ?? update.jurisdictionSlug}:${update.title.trim().toLowerCase()}`;
    const bucket = groups.get(key);
    if (bucket) bucket.push(update);
    else {
      groups.set(key, [update]);
      order.push(key);
    }
  }
  return order.map((key) => {
    const items = groups.get(key) ?? [];
    // The lead is the one that needs attention most: open findings, then the newest.
    const lead = [...items].sort(
      (a, b) =>
        (b.openFindings ?? 0) - (a.openFindings ?? 0) ||
        Date.parse(b.publishedAt ?? b.detectedAt) - Date.parse(a.publishedAt ?? a.detectedAt),
    )[0] as RegulatoryUpdate;
    return { key, lead, items };
  });
}

/** Which pile a publication belongs in, for a reader with ten minutes. */
export type Pile = "decide" | "relevant" | "rest";
export function pileOf(update: RegulatoryUpdate): Pile {
  const applicability = update.applicability ?? "needs_review";
  const proposed = update.applicabilityDecidedByEmail == null && applicability !== "needs_review";
  // A decision is owed where nobody has spoken, or where the agent says it
  // touches you and a person has not yet agreed. A proposed "not applicable"
  // is not a decision to make — it is a pile to skim, so it sits with the rest.
  if (applicability === "needs_review" || (proposed && applicability === "relevant")) return "decide";
  if (applicability === "relevant" || (update.openFindings ?? 0) > 0) return "relevant";
  return "rest";
}

/**
 * Where a publication stands, drawn the way Linear draws an issue's status:
 * documents it has opened are work in progress, a decision owed is a to-do,
 * a person's "relevant" is done, their "not applicable" is cancelled, and the
 * agent's unconfirmed "not applicable" waits in the backlog.
 */
export type PublicationState =
  | "findings"
  | "decide"
  | "handled"
  | "proposed_not_applicable"
  | "not_applicable";

export function publicationState(update: RegulatoryUpdate): PublicationState {
  if ((update.openFindings ?? 0) > 0) return "findings";
  if (pileOf(update) === "decide") return "decide";
  if ((update.applicability ?? "needs_review") === "not_applicable") {
    return update.applicabilityDecidedByEmail == null ? "proposed_not_applicable" : "not_applicable";
  }
  return "handled";
}

/**
 * Linear's priority column, answering "how much does this touch us": urgent
 * when it has opened findings in your documents, high when it is relevant,
 * medium while nobody has assessed it, none when it does not apply.
 */
export type ImpactLevel = "urgent" | "high" | "medium" | "none";
export const IMPACT_LABELS: Record<ImpactLevel, string> = {
  urgent: "Urgent — findings open in your documents",
  high: "High — touches this firm",
  medium: "Medium — not yet assessed",
  none: "None — does not apply",
};
export function impactLevel(update: RegulatoryUpdate): ImpactLevel {
  if ((update.openFindings ?? 0) > 0) return "urgent";
  const applicability = update.applicability ?? "needs_review";
  if (applicability === "relevant") return "high";
  if (applicability === "needs_review") return "medium";
  return "none";
}

